import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './login.css';
import { useTranslation } from 'react-i18next';



const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const checkUserAuthentication = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (token) {
          const response = await axios.get('/api/users/user', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (response.data.shop_name) {
            localStorage.setItem('shop', response.data.shop_name);
          }
          window.location.href = '/dashboard';
        }
      } catch (error) {
        localStorage.removeItem('access_token');
      }
    };

    checkUserAuthentication();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await axios.post('/api/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      // Store access token
      localStorage.setItem('access_token', response.data.access_token);

      // Fetch user data
      const userResponse = await axios.get('/api/users/user', {
        headers: {
          Authorization: `Bearer ${response.data.access_token}`,
        },
      });

      // Store user data including edn_api_account and fa_account if available
      const userData = {
        email: userResponse.data.email,
        role: userResponse.data.role,
        edn_api_account: userResponse.data.edn_api_account,
        edn_admin: userResponse.data.edn_admin,
        shop_name: userResponse.data.shop_name
      };
      
      if (userResponse.data.fa_account) {
        userData.fa_account = userResponse.data.fa_account;
      }
      
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('shop', userResponse.data.shop_name);

      // Add a small delay to ensure storage is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Final verification before navigation
      const finalCheck = {
        accessToken: localStorage.getItem('access_token'),
        shop: localStorage.getItem('shop')
      };
      console.log('Final storage check before navigation:', finalCheck);

      // Navigate if access token is properly stored
      if (finalCheck.accessToken) {
        window.location.href = '/dashboard';
      } else {
        console.error('Access token not properly stored before navigation');
        setLoginError(t('loginInvalidCredentials', 'Wrong email or password.'));
      }
    } catch (error) {
      console.error('Login failed:', error);
      localStorage.removeItem('access_token');
      localStorage.removeItem('userData');
      setLoginError(t('loginInvalidCredentials', 'Wrong email or password.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-shell">
        <section className="login-brand-panel" aria-label="Unanimity AI">
          <div className="login-brand-content">
            <img src="/menu_logo.png" alt={t('logo')} className="login-brand-logo" />
            <div>
              <p className="login-eyebrow">{t('loginSecureWorkspace', 'Secure workspace')}</p>
              <h1>{t('loginBrandTitle', 'Customer operations, unified.')}</h1>
              <p className="login-brand-copy">
                {t(
                  'loginBrandCopy',
                  'Sign in to manage conversations, automations, carrier rules and AI agent tools.'
                )}
              </p>
            </div>
          </div>
        </section>

        <div className="login-form">
          <div className="login-header">
            <img src="/menu_logo.png" alt={t('logo')} className="logo" />
          </div>
          <h2>{t('signIn')}</h2>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="email">{t('email')}</label>
              <input
                type="email"
                id="email"
                placeholder={t('enterEmail')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setLoginError('');
                }}
                aria-invalid={Boolean(loginError)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">{t('password')}</label>
              <input
                type="password"
                id="password"
                placeholder={t('enterPassword')}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setLoginError('');
                }}
                aria-invalid={Boolean(loginError)}
                required
              />
            </div>
            {loginError && (
              <div className="login-error-message" role="alert">
                {loginError}
              </div>
            )}
            <button type="submit" className="login-button" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading', 'Loading') : t('login')}
            </button>
          </form>
          <a href="/forgot-password">{t('forgotPassword')}</a>
        </div>
      </div>
    </div>
  );
};

export default Login;
